import algoliasearch from 'algoliasearch';
import React, { memo, useMemo } from 'react';
import { Configure, InstantSearch } from 'react-instantsearch-hooks-web';
import { CTA } from '../../interfaces/CTA.interface';
import { Theme } from '../../interfaces/enums/Theme.enum';
import { withBlokWrapper } from '../BlokWrapper/BlokWrapper';
import Heading, { HeadingTypes } from '../Heading/Heading';
import PageNotFoundSearch from './PageNotFoundSearch';
import Container from '../Utils/Container/Container';
import Grid from '../Utils/Grid/Grid';
import Col from '../Utils/Col/Col';
import { LinkProps } from '../../interfaces/LinkProps.interface';
import StoryblokLinkProvider from '../Utils/StoryblokLinkProvider/StoryblokLinkProvider';
import ButtonLink from '../ButtonLink/ButtonLink';
import { ButtonType } from '../../interfaces/enums/ButtonType.enum';
import { ButtonSize } from '../../interfaces/enums/ButtonSize.enum';
import { pushToDataLayer } from '../../utils/pushToDataLayer/pushToDataLayer.util';

interface PageNotFoundHeroProps {
  heading: string;
  body?: string;
  cta: LinkProps[];
  search_bar_placeholder: string;
  search_view_more_cta: CTA[];
  search_no_results_found_title: string;
  search_no_results_found_body?: string;
}

interface BlokProps {
  blok: PageNotFoundHeroProps;
}

const PageNotFoundHero: React.FC<BlokProps> = ({ blok }) => {
  const indexName: string = useMemo(() => {
    return `${process.env.GATSBY_ALGOLIA_SITE_SEARCH_INDEX_NAME}`;
  }, [process.env.GATSBY_ALGOLIA_SITE_SEARCH_INDEX_NAME]);

  const algoliaSearchClient = useMemo(() => {
    return algoliasearch(
      `${process.env.GATSBY_ALGOLIA_APP_ID}`,
      `${process.env.GATSBY_ALGOLIA_SEARCH_API_KEY}`
    );
  }, [
    process.env.GATSBY_ALGOLIA_APP_ID,
    process.env.GATSBY_ALGOLIA_SEARCH_API_KEY,
  ]);

  const overriddenSearchClient: any = useMemo(() => {
    return {
      ...algoliaSearchClient,
      search(requests: any[]) {
        if (requests.every(({ params }) => !params.query)) {
          return Promise.resolve({
            results: requests.map(() => ({
              hits: [],
              nbHits: 0,
              nbPages: 0,
              page: 0,
              processingTimeMS: 0,
              hitsPerPage: 0,
              exhaustiveNbHits: false,
              query: '',
              params: '',
            })),
          });
        }
        return algoliaSearchClient.search(requests);
      },
    };
  }, [algoliaSearchClient]);

  const ctaClicks =
    typeof window !== 'undefined' &&
    window.dataLayer?.filter((e) => e.event === '404_cta').length;

  return (
    <section className="relative">
      <div className="relative bg-[#0B212E]/80 py-20 lg:py-32">
        <Container>
          <Grid>
            <Col
              span={{ md: 10, lg: 8, xl: 6 }}
              start={{ md: 2, lg: 3, xl: 4 }}
            >
              <div className="w-full h-full flex items-center justify-center">
                <div className="w-full space-y-10 text-center">
                  <div className="space-y-4">
                    <Heading
                      text={blok.heading}
                      headingType={HeadingTypes.h1}
                    />
                    {blok?.body && (
                      <p className="text-text/body">{blok.body}</p>
                    )}
                  </div>
                  <div className="space-y-5">
                    <InstantSearch
                      indexName={indexName}
                      searchClient={overriddenSearchClient}
                    >
                      <Configure {...({ hitsPerPage: 3 } as any)} />
                      <PageNotFoundSearch
                        search_bar_placeholder={blok.search_bar_placeholder}
                        search_view_more_cta={blok.search_view_more_cta}
                        search_no_results_found_title={
                          blok.search_no_results_found_title
                        }
                        search_no_results_found_body={
                          blok.search_no_results_found_body
                        }
                      />
                    </InstantSearch>

                    <p className="text-text/body">OR</p>
                    {blok.cta.map((ctaItem: LinkProps) => (
                      <StoryblokLinkProvider
                        key={ctaItem._uid}
                        link={ctaItem.link}
                        render={(link) => (
                          <ButtonLink
                            url={link}
                            label={ctaItem.label}
                            type={ButtonType.TERTIARY}
                            size={ButtonSize.LG}
                            className="whitespace-nowrap"
                            onClick={() => {
                              const ctaClicks = window.dataLayer?.filter(
                                (e) => e.event === '404_cta'
                              ).length;

                              pushToDataLayer({
                                event: '404_site_search',
                                data: {
                                  '404_site_term': ctaClicks
                                    ? ctaClicks + 1
                                    : 1,
                                },
                              });
                            }}
                          >
                            {ctaItem.label}
                          </ButtonLink>
                        )}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </Col>
          </Grid>
        </Container>
      </div>
    </section>
  );
};

export default memo(withBlokWrapper(PageNotFoundHero, Theme.DARK));
